import { GalleryProps } from "@/components/Gallery/GalleryProps.ts";
import { DotButton } from "@/components/Gallery/components/GalleryDots/GalleryDots.tsx";
import { GalleryImage } from "@/components/Gallery/components/GalleryImage/GalleryImage.tsx";
import {
  NextButton,
  PrevButton,
} from "@/components/Gallery/components/GalleryNavigation/GalleryNavigation.tsx";
import { GalleryThumbnail } from "@/components/Gallery/components/GalleryThumbnail/GalleryThumbnail.tsx";
import { useGalleryCarousel } from "@/components/Gallery/hooks/useGalleryCarousel.ts";
import { ChevronLeft } from "@/components/Icons/utility/ChevronLeft.tsx";
import { ChevronRight } from "@/components/Icons/utility/ChevronRight.tsx";
import { Close } from "@/components/Icons/utility/Close.tsx";
import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { ImageOff } from "lucide-react";
import ReactDOM from "react-dom";

const GalleryPlaceholder = () => (
  <div className="c-gallery c-gallery--no-image">
    <div className="c-gallery__placeholder u-ratio u-ratio--16-9">
      <div className="u-ratio__inner">
        <span className="c-gallery__no-image-badge">
          <ImageOff size={20} strokeWidth={1.5} />
        </span>
      </div>
    </div>
  </div>
);

const EmblaCarousel = (props: GalleryProps) => {
  const { slides, thumbnails } = props;
  const { containerEl } = useShadowRoot();

  const {
    selectedIndex,
    lightbox,
    gallery,
    galleryThumbnails,
    scrollSnaps,
    onDotButtonClick,
    scrollPrev,
    scrollNext,
    onThumbClick,
    hasMultipleSlides,
  } = useGalleryCarousel({
    slides,
    options: props.options,
    keyboardNav: props.keyboardNav,
  });

  if (!slides?.length) return <GalleryPlaceholder />;

  const handleRenderSlides = () => (
    <div className="c-gallery__container">
      {slides.map((slide, index) => (
        <div
          className={`c-gallery__slide u-ratio u-ratio--16-9${
            props.lightbox ? " c-gallery__slide--clickable" : ""
          }`}
          key={index}
          onClick={props.lightbox ? () => lightbox.open(index) : undefined}
        >
          <figure className="u-ratio__inner">
            <GalleryImage url={slide.url} disabled={props.disabled} />
          </figure>
        </div>
      ))}
    </div>
  );

  const handleRenderThumbnailSlides = () => {
    if (slides.length < 1) return null;

    return (
      <div className="c-gallery-thumbnails__container">
        {slides.map((slide, index) => (
          <GalleryThumbnail
            onClick={() => onThumbClick(index)}
            selected={index === selectedIndex}
            imgSrc={slide.url}
            key={index}
          />
        ))}
      </div>
    );
  };

  const handleRenderLightbox = () => {
    if (!props.lightbox || !lightbox.isOpen) return null;

    return ReactDOM.createPortal(
      <div className="c-gallery-lightbox" onClick={lightbox.close} onMouseDown={(e) => e.stopPropagation()}>
        <div className="c-gallery-lightbox__content" onClick={(e) => e.stopPropagation()}>
          <button className="c-gallery-lightbox__close c-close-button" onClick={lightbox.close}>
            <Close />
          </button>
          {hasMultipleSlides && (
            <div className="c-gallery-lightbox__buttons">
              <PrevButton onClick={lightbox.prev}>
                <ChevronLeft />
              </PrevButton>
              <NextButton onClick={lightbox.next}>
                <ChevronRight />
              </NextButton>
            </div>
          )}
          <img className="c-gallery-lightbox__image" src={slides[lightbox.index]?.url} alt="SWEN" />
        </div>
        {hasMultipleSlides && (
          <div className="c-gallery-lightbox__counter">
            {lightbox.index + 1} / {slides.length}
          </div>
        )}
      </div>,
      containerEl,
    );
  };

  return (
    <div className="c-gallery">
      <div className="c-gallery__viewport" ref={gallery}>
        {handleRenderSlides()}
        {hasMultipleSlides && (
          <div className="c-gallery__buttons">
            <PrevButton onClick={scrollPrev}>
              <ChevronLeft />
            </PrevButton>
            <NextButton onClick={scrollNext}>
              <ChevronRight />
            </NextButton>
          </div>
        )}
        {thumbnails ||
          (hasMultipleSlides && (
            <div className="embla__controls">
              <div className="embla__dots">
                {(() => {
                  const MAX_DOTS = 5;
                  const total = scrollSnaps?.length ?? 0;
                  const visibleCount = Math.min(total, MAX_DOTS);
                  const hasOverflow = total > MAX_DOTS;

                  return scrollSnaps?.slice(0, visibleCount).map((_, index) => {
                    const isLast = index === visibleCount - 1;
                    const isOverflowDot = isLast && hasOverflow;
                    const isSelected = isOverflowDot
                      ? selectedIndex >= visibleCount - 1
                      : index === selectedIndex;

                    return (
                      <DotButton
                        key={index}
                        onClick={() => onDotButtonClick(isOverflowDot ? selectedIndex : index)}
                        className={`embla__dot${isSelected ? " embla__dot--selected" : ""}${
                          isOverflowDot ? " embla__dot--overflow" : ""
                        }`}
                      >
                        {isOverflowDot ? `+${total - (MAX_DOTS - 1)}` : null}
                      </DotButton>
                    );
                  });
                })()}
              </div>
            </div>
          ))}
      </div>
      {thumbnails && (
        <div className="c-gallery-thumbnails">
          <div className="c-gallery-thumbnails__viewport" ref={galleryThumbnails}>
            {handleRenderThumbnailSlides()}
          </div>
        </div>
      )}
      {handleRenderLightbox()}
    </div>
  );
};

export default EmblaCarousel;
